/**
 * Phase 6 — Buyer "Manage your data" drawer.
 *
 * Two actions:
 *   - Download my data (calls POST /api/quotes/{id}/data-export and saves JSON)
 *   - Delete my data on this quote (calls POST /api/quotes/{id}/delete-data
 *     after a confirm-by-typing-DELETE guard)
 *
 * Visible only when the buyer is authenticated and owns the quote.
 */
import React, { useState } from "react";
import { ShieldCheck, Download, Trash2, X, Loader2 } from "lucide-react";

import { exportQuoteData, deleteQuoteData } from "../../lib/api";
import { toast } from "../ui/sonner";

export default function ManageDataDrawer({ quoteId, onDeleted }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        data-testid="manage-data-entry"
        className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1A1A1A]/10 bg-white px-5 py-4 sm:px-6"
      >
        <div className="min-w-0">
          <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[#9A7A2F]">
            <ShieldCheck className="h-3.5 w-3.5" />
            Privacy
          </p>
          <p className="mt-1 font-display text-base text-[#1A1A1A]">
            Manage your data
          </p>
          <p className="text-xs text-[#1A1A1A]/55">
            Download or delete everything we hold about this request.
          </p>
        </div>
        <button
          type="button"
          data-testid="manage-data-open"
          onClick={() => setOpen(true)}
          className="rounded-full border border-[#1A1A1A]/15 px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/75 hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
        >
          Open
        </button>
      </div>
      {open ? (
        <Drawer
          quoteId={quoteId}
          onClose={() => setOpen(false)}
          onDeleted={onDeleted}
        />
      ) : null}
    </>
  );
}

function Drawer({ quoteId, onClose, onDeleted }) {
  const [exporting, setExporting] = useState(false);
  const [deletePhase, setDeletePhase] = useState("idle"); // idle | confirm | working | done
  const [confirmText, setConfirmText] = useState("");

  async function onExport() {
    setExporting(true);
    try {
      const res = await exportQuoteData(quoteId);
      const filename = res.filename || `shutterkart-data-${quoteId}.json`;
      const blob = new Blob([JSON.stringify(res.bundle || {}, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't generate your data export.",
      );
    } finally {
      setExporting(false);
    }
  }

  async function onDelete() {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type "DELETE" to confirm.');
      return;
    }
    setDeletePhase("working");
    try {
      const res = await deleteQuoteData(quoteId);
      setDeletePhase("done");
      toast.success("Your data on this request has been deleted.");
      onDeleted?.(res);
    } catch (e) {
      setDeletePhase("confirm");
      toast.error(e?.response?.data?.detail || "Couldn't delete right now.");
    }
  }

  return (
    <div
      data-testid="manage-data-drawer"
      className="fixed inset-0 z-[60] flex bg-[#1A1A1A]/55"
      onClick={onClose}
    >
      <div
        className="ml-auto h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#9A7A2F]">
              Privacy
            </p>
            <h2 className="mt-1 font-display text-2xl text-[#1A1A1A]">
              Manage your data
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="manage-data-close"
            className="rounded-full p-2 hover:bg-[#1A1A1A]/5"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <section className="mt-6 rounded-2xl border border-[#1A1A1A]/10 p-4">
          <p className="font-display text-base text-[#1A1A1A]">
            Download my data
          </p>
          <p className="mt-1 text-xs text-[#1A1A1A]/65">
            Get a JSON file with everything we hold about this quote — your
            brief, conversation messages, attachments and the quote response.
          </p>
          <button
            type="button"
            data-testid="manage-data-export"
            onClick={onExport}
            disabled={exporting}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-[#FAF7F2] hover:bg-[#2a2a2a] disabled:opacity-60"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {exporting ? "Preparing…" : "Download JSON"}
          </button>
        </section>

        <section className="mt-4 rounded-2xl border border-[#B0413E]/30 p-4">
          <p className="font-display text-base text-[#B0413E]">
            Delete my data on this quote
          </p>
          <p className="mt-1 text-xs text-[#1A1A1A]/65">
            This anonymises your contact info, replaces your messages with
            <code className="mx-1 rounded bg-[#1A1A1A]/5 px-1.5 py-0.5 font-mono text-[10px]">[deleted]</code>,
            and removes any reference images. The quote thread stays closed.
            This action can't be undone.
          </p>
          {deletePhase === "idle" ? (
            <button
              type="button"
              data-testid="manage-data-delete-start"
              onClick={() => setDeletePhase("confirm")}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#B0413E] px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-[#B0413E] hover:bg-[#B0413E]/8"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete my data
            </button>
          ) : null}
          {deletePhase === "confirm" || deletePhase === "working" ? (
            <div className="mt-3 space-y-3">
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
                Type <span className="text-[#B0413E]">DELETE</span> to confirm
                <input
                  data-testid="manage-data-delete-confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[#B0413E]/30 bg-white px-2 py-1.5 text-sm normal-case tracking-normal text-[#1A1A1A] focus:border-[#B0413E] focus:outline-none"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="manage-data-delete-cancel"
                  onClick={() => {
                    setDeletePhase("idle");
                    setConfirmText("");
                  }}
                  className="rounded-full border border-[#1A1A1A]/15 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A]/65 hover:border-[#1A1A1A]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="manage-data-delete-confirm"
                  disabled={deletePhase === "working"}
                  onClick={onDelete}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#B0413E] px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-[#FAF7F2] hover:bg-[#9A2D2A] disabled:opacity-60"
                >
                  {deletePhase === "working" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  {deletePhase === "working" ? "Deleting…" : "Confirm delete"}
                </button>
              </div>
            </div>
          ) : null}
          {deletePhase === "done" ? (
            <p
              data-testid="manage-data-delete-done"
              className="mt-3 rounded-lg bg-[#4F8060]/10 px-3 py-2 text-xs text-[#3B5F47]"
            >
              Your data on this quote has been deleted.
            </p>
          ) : null}
        </section>

        <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/45">
          Need help? <a className="underline" href="mailto:hello@shutterkart.com">hello@shutterkart.com</a>
        </p>
      </div>
    </div>
  );
}
